import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Loader2,
  ArrowRight,
  ArrowLeft,
  Eye,
  EyeOff,
  Check,
  UserCircle2,
  Globe2,
  ShieldCheck,
  Landmark,
  Lock,
  Quote,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { analytics } from "@/services/analytics";
import Logo from "@/components/Logo";
import { useRecaptcha } from "@/hooks/useRecaptcha";
import FlowBackdrop from "@/components/illustrations/FlowBackdrop";

// ─── Shared helpers ───────────────────────────────────────────────────────────
function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block space-y-1.5 ${className}`}>
      <span className="font-heading text-[12.5px] font-semibold text-[hsl(var(--zt-teal))]">
        {label}
        {required && (
          <span className="text-[hsl(var(--zt-gold))] ml-0.5" aria-hidden>
            *
          </span>
        )}
      </span>
      {children}
    </label>
  );
}

function RadioGroup({ name, options, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2 pt-0.5">
      {options.map((opt) => {
        const active = value === opt;
        return (
          <label
            key={opt}
            className={`inline-flex items-center gap-2 cursor-pointer rounded-lg border px-3 py-2 transition-all duration-200 ${
              active
                ? "border-[hsl(var(--zt-gold))] bg-[hsl(var(--zt-gold))]/8"
                : "border-[hsl(var(--zt-beige-deep))] bg-white hover:border-[hsl(var(--zt-teal))]/25"
            }`}
          >
            <input
              type="radio"
              name={name}
              value={opt}
              checked={active}
              onChange={() => onChange(opt)}
              className="sr-only"
            />
            <span
              className={`w-4 h-4 rounded-full border grid place-items-center transition-all duration-200 ${
                active ? "border-[hsl(var(--zt-gold))] bg-[hsl(var(--zt-gold))]" : "border-[hsl(var(--zt-muted))]/40"
              }`}
            >
              {active && <Check size={10} className="text-white" strokeWidth={3} />}
            </span>
            <span
              className={`font-heading text-[12.5px] font-medium ${
                active ? "text-[hsl(var(--zt-teal))]" : "text-[hsl(var(--zt-charcoal))]/80"
              }`}
            >
              {opt}
            </span>
          </label>
        );
      })}
    </div>
  );
}

function SectionCard({ icon: Icon, step, title, subtitle, children }) {
  return (
    <section className="rounded-2xl bg-white border border-[hsl(var(--zt-teal))]/10 p-5 lg:p-6 elev-1 space-y-5">
      <header className="flex items-start gap-3.5">
        <div className="w-10 h-10 rounded-xl bg-[hsl(var(--zt-gold))]/10 grid place-items-center flex-shrink-0">
          <Icon size={17} className="text-[hsl(var(--zt-gold))]" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-numeric text-[11px] font-semibold text-[hsl(var(--zt-muted))]">{step}</span>
            <h2 className="font-heading text-[15px] font-semibold text-[hsl(var(--zt-teal))]">{title}</h2>
          </div>
          <p className="font-body text-[12.5px] text-[hsl(var(--zt-charcoal))]/70 mt-1 leading-relaxed">{subtitle}</p>
        </div>
      </header>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

const ASSURANCES = [
  { icon: ShieldCheck, text: "SEBI-registered advisory partners" },
  { icon: Landmark, text: "Funds move only via your own NRE / NRO accounts" },
  { icon: Lock, text: "AES-256 at rest · TLS 1.3 in transit" },
];

const surveyInitial = {
  country_of_residence: "",
  years_abroad: "",
  oci_card: "",
  oci_card_other: "",
  heard_gift_city: "",
  invested_gift_city: "",
  nri_app_frustrations: "",
  wants_educational_content: "",
};

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const { execute: executeRecaptcha } = useRecaptcha();

  // Account fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [yearsAbroadError, setYearsAbroadError] = useState("");

  // Survey fields
  const [survey, setSurvey] = useState(surveyInitial);
  const [busy, setBusy] = useState(false);

  const updateSurvey = (k) => (val) => setSurvey((d) => ({ ...d, [k]: val }));
  const updateSurveyText = (k) => (e) => setSurvey((d) => ({ ...d, [k]: e.target.value }));

  const validateEmail = (val) => {
    if (!val) return "Email is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(val)) return "Enter a valid email address.";
    return "";
  };

  const validatePassword = (val) => {
    if (!val) return "Password is required.";
    if (val.length < 8) return "Must be at least 8 characters.";
    if (!/[A-Z]/.test(val)) return "Must include at least one uppercase letter.";
    if (!/[a-z]/.test(val)) return "Must include at least one lowercase letter.";
    if (!/[0-9]/.test(val)) return "Must include at least one number.";
    if (!/[^A-Za-z0-9]/.test(val)) return "Must include at least one special character.";
    return "";
  };

  const validateYearsAbroad = (val) => {
    if (!val.trim()) return "This field is required.";
    if (/^\d+$/.test(val.trim())) return "Please include a unit, e.g. '5 years' or '6 months'.";
    if (!/^(\d+\s*(year|years|month|months|yr|yrs|mo|mos))(\s+\d+\s*(month|months|mo|mos))?$/i.test(val.trim()))
      return "Enter a duration like '5 years', '6 months', or '2 years 3 months'.";
    return "";
  };

  const passwordStrength = (val) => {
    let score = 0;
    if (val.length >= 8) score++;
    if (/[A-Z]/.test(val)) score++;
    if (/[a-z]/.test(val)) score++;
    if (/[0-9]/.test(val)) score++;
    if (/[^A-Za-z0-9]/.test(val)) score++;
    return score;
  };

  const strength = passwordStrength(password);
  const strengthLabel = ["", "Weak", "Weak", "Fair", "Good", "Strong"][strength];
  const strengthColor = [
    "",
    "hsl(var(--zt-negative))",
    "hsl(var(--zt-negative))",
    "#D69E2E",
    "hsl(var(--zt-positive))",
    "hsl(var(--zt-sage))",
  ][strength];

  const allRequiredFilled =
    name.trim() &&
    email.trim() &&
    !validateEmail(email) &&
    !validatePassword(password) &&
    survey.country_of_residence.trim() &&
    survey.years_abroad.trim() &&
    !validateYearsAbroad(survey.years_abroad) &&
    survey.oci_card &&
    (survey.oci_card !== "Other" || survey.oci_card_other.trim()) &&
    survey.heard_gift_city &&
    survey.invested_gift_city &&
    survey.nri_app_frustrations.trim() &&
    survey.wants_educational_content;

  const submit = async (e) => {
    e.preventDefault();
    const eErr = validateEmail(email);
    const pErr = validatePassword(password);
    const yErr = validateYearsAbroad(survey.years_abroad);
    setEmailError(eErr);
    setPasswordError(pErr);
    setYearsAbroadError(yErr);
    if (eErr || pErr || yErr) return;
    const requiredSurvey = [
      "country_of_residence",
      "years_abroad",
      "oci_card",
      "heard_gift_city",
      "invested_gift_city",
      "nri_app_frustrations",
      "wants_educational_content",
    ];
    if (requiredSurvey.some((k) => !survey[k])) {
      toast.error("Please fill in all required survey fields.");
      return;
    }
    if (survey.oci_card === "Other" && !survey.oci_card_other.trim()) {
      toast.error("Please specify your OCI card status.");
      return;
    }
    setBusy(true);
    try {
      const recaptchaToken = await executeRecaptcha("register");
      await register(name, email, password, recaptchaToken);
      analytics.registration("email");
      // Submit survey linked to the new user's name
      const payload = { ...survey, name };
      if (payload.oci_card !== "Other") delete payload.oci_card_other;
      try {
        await api.post("/survey", payload);
      } catch (_e) {
        // Survey failure is non-blocking
      }
      toast.success("Welcome to Zenstrust.");
      navigate("/");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1fr_0.8fr] bg-[hsl(var(--zt-beige))]">
      {/* ── Form column ─────────────────────────────────────────── */}
      <div className="relative px-6 py-8 sm:px-10 lg:px-14 xl:px-20">
        <div aria-hidden className="absolute inset-0 dot-grid opacity-40 pointer-events-none" />

        <div className="relative flex items-center justify-between">
          <Logo size="h-12" to="/" className="transition-transform duration-300 hover:scale-[1.02]" />
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 font-heading text-[12.5px] font-medium text-[hsl(var(--zt-muted))] hover:text-[hsl(var(--zt-teal))] transition-colors duration-200"
          >
            <ArrowLeft size={14} /> Back to site
          </Link>
        </div>

        <div className="relative max-w-[560px] mx-auto py-10 space-y-7 animate-fade-up">
          <div className="space-y-3">
            <span className="eyebrow">Create your account</span>
            <h1 className="font-display text-[1.9rem] sm:text-[2.15rem] leading-[1.15] text-[hsl(var(--zt-teal))]">
              Start your <span className="text-[hsl(var(--zt-gold))]">India chapter</span>.
            </h1>
            <p className="font-body text-[14.5px] text-[hsl(var(--zt-charcoal))]/80 leading-relaxed">
              Two short steps. Your answers shape the advisor and strategy we pair you with — no documents
              needed yet.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
            {/* ── Step 1: account ── */}
            <SectionCard
              icon={UserCircle2}
              step="01"
              title="Your account"
              subtitle="Used to secure your login and portfolio access."
            >
              <Field label="Full name" required>
                <input
                  required
                  autoComplete="name"
                  data-testid="signup-name"
                  className="input-editorial font-body"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Anika Reddy"
                />
              </Field>

              <Field label="Email address" required>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  data-testid="signup-email"
                  className={`input-editorial font-body ${emailError ? "!border-[hsl(var(--zt-negative))]" : ""}`}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError(validateEmail(e.target.value));
                  }}
                  onBlur={(e) => setEmailError(validateEmail(e.target.value))}
                  placeholder="you@example.com"
                />
                {emailError && (
                  <p className="text-[hsl(var(--zt-negative))] text-xs mt-1.5 font-body">{emailError}</p>
                )}
              </Field>

              <Field label="Password" required>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    autoComplete="new-password"
                    data-testid="signup-password"
                    className={`input-editorial has-affix font-body ${passwordError ? "!border-[hsl(var(--zt-negative))]" : ""}`}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordError(validatePassword(e.target.value));
                    }}
                    onBlur={(e) => setPasswordError(validatePassword(e.target.value))}
                    placeholder="At least 8 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center rounded-lg text-[hsl(var(--zt-muted))] hover:text-[hsl(var(--zt-teal))] hover:bg-[hsl(var(--zt-beige))] transition-colors duration-200"
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>

                {password ? (
                  <div className="mt-2.5 space-y-1.5">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div
                          key={i}
                          className="h-1 flex-1 rounded-full transition-all duration-300"
                          style={{ background: i <= strength ? strengthColor : "hsl(var(--zt-beige-deep))" }}
                        />
                      ))}
                    </div>
                    <p className="font-heading text-[11.5px] font-semibold" style={{ color: strengthColor }}>
                      {strengthLabel}
                    </p>
                  </div>
                ) : (
                  <p className="font-body text-[11.5px] text-[hsl(var(--zt-muted))] mt-1.5">
                    Min 8 characters with an uppercase, lowercase, number and symbol.
                  </p>
                )}
                {passwordError && (
                  <p className="text-[hsl(var(--zt-negative))] text-xs mt-1.5 font-body">{passwordError}</p>
                )}
              </Field>
            </SectionCard>

            {/* ── Step 2: NRI profile ── */}
            <SectionCard
              icon={Globe2}
              step="02"
              title="Your NRI profile"
              subtitle="Helps us match your residency, tax status and goals."
            >
              <Field label="Country of residence" required>
                <input
                  className="input-editorial font-body"
                  value={survey.country_of_residence}
                  onChange={updateSurveyText("country_of_residence")}
                  placeholder="e.g. United States"
                  required
                />
              </Field>

              <Field label="How long have you been living abroad?" required>
                <input
                  className={`input-editorial font-body ${yearsAbroadError ? "!border-[hsl(var(--zt-negative))]" : ""}`}
                  value={survey.years_abroad}
                  onChange={(e) => {
                    updateSurveyText("years_abroad")(e);
                    setYearsAbroadError(validateYearsAbroad(e.target.value));
                  }}
                  onBlur={(e) => setYearsAbroadError(validateYearsAbroad(e.target.value))}
                  placeholder="e.g. 5 years or 6 months"
                  required
                />
                {yearsAbroadError && (
                  <p className="text-[hsl(var(--zt-negative))] text-xs mt-1.5 font-body">{yearsAbroadError}</p>
                )}
              </Field>

              <Field label="Do you have an OCI card?" required>
                <RadioGroup
                  name="oci_card"
                  options={["Yes", "No", "Other (visa, etc.)"]}
                  value={survey.oci_card === "Other" ? "Other (visa, etc.)" : survey.oci_card}
                  onChange={(val) => {
                    const normalized = val.startsWith("Other") ? "Other" : val;
                    setSurvey((d) => ({
                      ...d,
                      oci_card: normalized,
                      oci_card_other: normalized !== "Other" ? "" : d.oci_card_other,
                    }));
                  }}
                />
                {survey.oci_card === "Other" && (
                  <input
                    className="input-editorial font-body mt-2.5"
                    value={survey.oci_card_other}
                    onChange={updateSurveyText("oci_card_other")}
                    placeholder="Please specify…"
                  />
                )}
              </Field>

              <Field label="Have you heard about GIFT City?" required>
                <RadioGroup
                  name="heard_gift_city"
                  options={["Yes", "No"]}
                  value={survey.heard_gift_city}
                  onChange={updateSurvey("heard_gift_city")}
                />
              </Field>

              <Field label="Have you invested, or are you exploring GIFT City?" required>
                <RadioGroup
                  name="invested_gift_city"
                  options={["Yes", "No", "Maybe"]}
                  value={survey.invested_gift_city}
                  onChange={updateSurvey("invested_gift_city")}
                />
              </Field>

              <Field label="Biggest frustrations with current NRI investment apps?" required>
                <textarea
                  className="input-editorial font-body resize-none"
                  rows={3}
                  value={survey.nri_app_frustrations}
                  onChange={updateSurveyText("nri_app_frustrations")}
                  placeholder="Share your thoughts…"
                />
              </Field>

              <Field label="Would you like educational content in the app?" required>
                <RadioGroup
                  name="wants_educational_content"
                  options={["Yes", "No", "Maybe"]}
                  value={survey.wants_educational_content}
                  onChange={updateSurvey("wants_educational_content")}
                />
              </Field>
            </SectionCard>

            <div className="space-y-3 pt-1">
              <button
                type="submit"
                disabled={busy || !allRequiredFilled}
                data-testid="signup-submit"
                className="w-full bg-[hsl(var(--zt-gold))] text-white hover:bg-[hsl(var(--zt-gold))]/92 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 rounded-xl h-12 font-heading text-sm font-semibold tracking-tight flex items-center justify-center gap-2 elev-2 transition-all duration-200 hover:-translate-y-0.5"
              >
                {busy ? <Loader2 className="animate-spin" size={16} /> : null}
                {busy ? "Creating account…" : "Create account"}
                {!busy && <ArrowRight size={16} />}
              </button>

              <p className="flex items-start gap-2 font-body text-[11.5px] text-[hsl(var(--zt-muted))] leading-relaxed">
                <Lock size={13} className="mt-0.5 flex-shrink-0 text-[hsl(var(--zt-sage))]" />
                By creating an account you agree to our terms and privacy policy. Your details are encrypted
                and never sold.
              </p>
            </div>
          </form>

          <p className="font-body text-[13.5px] text-[hsl(var(--zt-charcoal))]/75 text-center">
            Already have an account?{" "}
            <Link
              to="/login"
              data-testid="signup-to-login"
              className="font-heading font-semibold text-[hsl(var(--zt-gold))] underline-offset-4 hover:underline"
            >
              Log in
            </Link>
          </p>
        </div>
      </div>

      {/* ── Visual column ───────────────────────────────────────── */}
      <div className="hidden lg:flex sticky top-0 h-screen relative overflow-hidden bg-[hsl(var(--zt-teal))] flex-col justify-between p-12 xl:p-14">
        <div aria-hidden className="absolute inset-0 dot-grid-inverse opacity-30 pointer-events-none" />
        <FlowBackdrop variant="dark" pattern="z" className="opacity-60" />
        <div
          aria-hidden
          className="absolute top-[-15%] right-[-20%] w-[520px] h-[520px] rounded-full opacity-[0.18] blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, #4F63F0 0%, transparent 65%)" }}
        />

        <div className="relative max-w-md">
          <span className="gold-rule" />
          <h2 className="font-display text-[2rem] xl:text-[2.3rem] leading-[1.12] text-white mt-5">
            Join 2,400+ NRIs investing back home with{" "}
            <span className="text-[hsl(var(--zt-gold-soft))]">clarity</span>.
          </h2>
          <p className="font-body text-[14px] text-white/60 leading-relaxed mt-4">
            One dashboard for portfolios, advisors, KYC and filings — across 32 countries.
          </p>
        </div>

        <div className="relative rounded-2xl border border-white/10 bg-white/[0.05] backdrop-blur-sm p-6 max-w-md">
          <Quote size={18} className="text-[hsl(var(--zt-gold-soft))]" />
          <p className="font-body text-[15px] leading-relaxed text-white/85 mt-3">
            Zenstrust gave me the clarity I'd been searching for since I left India in 2009. One team, one
            dashboard, no chasing paperwork across time zones.
          </p>
          <div className="mt-5 flex items-center gap-3 border-t border-white/10 pt-4">
            <div className="w-9 h-9 rounded-full bg-[hsl(var(--zt-gold))]/25 border border-[hsl(var(--zt-gold))]/40 grid place-items-center font-heading text-[13px] font-bold text-white">
              R
            </div>
            <div>
              <p className="font-heading text-[13px] font-semibold text-white">Ravi Menon</p>
              <p className="font-heading text-[11px] text-white/50 font-medium mt-0.5">
                London · Member since 2023
              </p>
            </div>
          </div>
        </div>

        <ul className="relative space-y-2.5 max-w-md">
          {ASSURANCES.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-center gap-2.5">
              <Icon size={14} className="text-[hsl(var(--zt-sage))] flex-shrink-0" />
              <span className="font-heading text-[12.5px] text-white/70 font-medium">{text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
